import React from 'react';

import type { Text } from '@zillow/constellation';
import { FormField, Input, Label, Spacer } from '@zillow/constellation';

import OptionSelector from '../Utilities/OptionSelector';
import HeaderToggle from '../Utilities/HeaderToggle';
import MultiFamilyLandlordUtility, { isMultiFamily } from '../Utilities/MultiFamilyLandlordUtility';
import type { Section } from '../../../../models/index';
import type { HTMLElementEvent } from '../../../../models/Types';
import { NATURAL_GAS } from '../../../../constants';
import SwitchWithTitle from '../../../Widgets/SwitchWithTitle';
import { CenteredHR } from '../../../Widgets/CenteredHR';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function NaturalGas_v2({ section, setSectionValues }: Props): JSX.Element {
  const isMultiFamilyLandlord = isMultiFamily(section);
  const naturalGas_isEnabled = section.getFieldValue(NATURAL_GAS.FIELD_NAME.IS_ENABLED);
  const tenantSelected = section.getFieldValue(NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY);
  const landlordSelected = section.getFieldValue(NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const naturalGas_tenantPaysInAdditionToRent = section.getFieldValue(
    NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
  );

  function setLandlordResponsibility(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY]: true,
    });
  }

  function setTenantResponsibility(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY]: true,
      [NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
      [NATURAL_GAS.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.FORMULA]: '',
      [NATURAL_GAS.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.MONTHLY_COST_PERCENTAGE]: '',
    });
  }

  function setServiceProvider(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(NATURAL_GAS.FIELD_NAME.IS_ENABLED),
    });
  }

  function togglePaysInAddition(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: !section.getFieldValue(
        NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
      ),
      [NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Natural gas"
        subheader="Applies to this property"
        switchOn={naturalGas_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {naturalGas_isEnabled && (
        <Spacer marginBottom="sm">
          <CenteredHR />
          <SwitchWithTitle
            text="Tenant pays in addition to rent"
            onClick={togglePaysInAddition}
            toggled={naturalGas_tenantPaysInAdditionToRent}
          />
        </Spacer>
      )}
      {naturalGas_tenantPaysInAdditionToRent && (
        <>
          <Spacer marginBottom="sm">
            <OptionSelector
              disabled={!naturalGas_isEnabled}
              optionsText="Who will be responsible for natural gas set up?"
              option1Content="Responsible for account set up and direct payments to specified service provider."
              option2Content={
                isMultiFamilyLandlord
                  ? 'Responsible for account set up and will bill tenant separately.'
                  : 'Responsible for account set up and will bill tenant ' + 'a reasonable proportion separately.'
              }
              option1Selected={tenantSelected}
              onOption1Click={setTenantResponsibility}
              option2Selected={landlordSelected}
              onOption2Click={setLandlordResponsibility}
            />
          </Spacer>

          {tenantSelected && (
            <FormField>
              <Label id="service-provider">What service provider?</Label>
              <Input
                aria-labelledby="service-provider"
                placeholder="(example: National Natural Gas Co.)"
                value={section.getFieldValue(NATURAL_GAS.FIELD_NAME.SERVICE_PROVIDER)}
                onChange={setServiceProvider}
              />
            </FormField>
          )}

          {isMultiFamilyLandlord && landlordSelected && (
            <MultiFamilyLandlordUtility
              section={section}
              utilityName="natural gas"
              percentageFieldName={NATURAL_GAS.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.MONTHLY_COST_PERCENTAGE}
              formulaFieldName={NATURAL_GAS.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.FORMULA}
              setSectionValues={setSectionValues}
            />
          )}
        </>
      )}
    </>
  );
}
